import { useMemo } from 'react';
import clsx from 'clsx';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { FILTER_UI_ICONS } from './filterIcons';
import { filterItemSelectedClass, filterFieldLabelClass, filterFieldTriggerClass } from './styles';

const ALL_OPTION_ID = '__all__';

type FilterOption = { id: string; label: string };

const normalizeOptions = (options: (string | FilterOption)[]) =>
  options.map((option) => (typeof option === 'string' ? { id: option, label: option } : option));

type FilterCascadeSelectProps = {
  label: string;
  placeholder: string;
  value: string;
  options: (string | FilterOption)[];
  onChange: (value: string) => void;
  disabled?: boolean;
  listClassName?: string;
};

/** Un filtro a scelta singola: la voce del segnaposto toglie il filtro. */
export default function FilterCascadeSelect({
  label,
  placeholder,
  value,
  options,
  onChange,
  disabled = false,
  listClassName = 'max-h-44',
}: FilterCascadeSelectProps) {
  const isActive = Boolean(value);
  const items = useMemo(() => normalizeOptions(options), [options]);
  const selected = items.find((item) => item.id === value);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={disabled}
        className={clsx('group', filterFieldTriggerClass({ active: isActive }))}
      >
        <span className={filterFieldLabelClass}>{label}</span>
        <span className="flex w-full items-center justify-between gap-2 text-caption font-semibold">
          <span className={clsx('min-w-0 truncate', isActive ? 'text-brand' : 'text-subtle')}>
            {value ? (selected?.label ?? value) : placeholder}
          </span>
          <FILTER_UI_ICONS.chevronDown
            aria-hidden
            className="h-3.5 w-3.5 shrink-0 transition-transform group-data-[state=open]:rotate-180"
          />
        </span>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        sideOffset={6}
        align="start"
        className={clsx(
          'flex w-(--radix-dropdown-menu-trigger-width) flex-col gap-1',
          listClassName
        )}
      >
        <DropdownMenuRadioGroup
          value={value || ALL_OPTION_ID}
          onValueChange={(next) => onChange(next === ALL_OPTION_ID ? '' : next)}
          className="flex flex-col gap-1"
        >
          <DropdownMenuRadioItem
            value={ALL_OPTION_ID}
            className={filterItemSelectedClass}
            indicatorClassName="text-muted"
          >
            <span className="min-w-0 flex-1 font-medium text-muted">{placeholder}</span>
          </DropdownMenuRadioItem>
          {items.map((item) => (
            <DropdownMenuRadioItem
              key={item.id}
              value={item.id}
              className={filterItemSelectedClass}
              indicatorClassName="text-muted"
            >
              <span className="min-w-0 flex-1 font-medium">{item.label}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
